import type * as React from 'react';
import { useCallback, useEffect, useId, useRef } from 'react';

import { ModalSize } from '../../utils';

import './Modal.css';

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"], iframe, audio[controls], video[controls]';

interface ModalProps {
  /**
   * Whether the modal is currently visible
   */
  isOpen: boolean;
  /**
   * Callback when the modal is closed (via close button, overlay click, or Escape key)
   */
  onClose: () => void;
  /**
   * Content to render inside the modal body
   */
  children?: React.ReactNode;
  /**
   * Title text displayed in the modal header
   */
  heading?: string;
  /**
   * Size variant of the modal
   * @default ModalSize.Medium
   */
  size?: ModalSize;
  /**
   * Additional CSS class name for the modal content wrapper
   */
  className?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
  /**
   * When true, disables closing via Escape, overlay click, or the close button.
   */
  preventDismiss?: boolean;
}

function Modal({
  isOpen,
  onClose,
  children,
  heading,
  size = ModalSize.Medium,
  className,
  preventDismiss = false,
  'data-testid': dataTestId,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const headingId = useId();
  const baseTestId = dataTestId ?? 'modal';

  const restoreFocus = useCallback(() => {
    const previouslyFocusedElement = previouslyFocusedElementRef.current;

    if (previouslyFocusedElement && document.contains(previouslyFocusedElement)) {
      previouslyFocusedElement.focus();
    }

    previouslyFocusedElementRef.current = null;
  }, []);

  useEffect(() => {
    if (isOpen) {
      const activeElement = document.activeElement;
      previouslyFocusedElementRef.current = activeElement instanceof HTMLElement ? activeElement : null;
      document.body.style.overflow = 'hidden';
      modalRef.current?.focus();
    } else {
      document.body.style.overflow = '';
      restoreFocus();
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, restoreFocus]);

  useEffect(() => restoreFocus, [restoreFocus]);

  const getFocusableElements = useCallback(() => {
    if (!modalRef.current) return [];

    return Array.from(modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
      const isDisabled = element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true';
      const isHiddenInput = element.matches('input[type="hidden"]');
      const isHiddenByStyle =
        ['none', 'hidden'].includes(getComputedStyle(element).display) ||
        getComputedStyle(element).visibility === 'hidden';
      const isHidden =
        element.hidden || isHiddenByStyle || element.closest('[hidden], [inert], [aria-hidden="true"]') !== null;

      return !isDisabled && !isHiddenInput && element.tabIndex >= 0 && !isHidden;
    });
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();

      if (!preventDismiss) onClose();

      return;
    }

    if (e.key !== 'Tab') return;

    e.stopPropagation();

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) {
      e.preventDefault();
      modalRef.current?.focus();

      return;
    }

    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

    if (e.shiftKey && (currentIndex <= 0 || currentIndex === -1)) {
      e.preventDefault();
      focusableElements[focusableElements.length - 1].focus();
    } else if (!e.shiftKey && (currentIndex === -1 || currentIndex === focusableElements.length - 1)) {
      e.preventDefault();
      focusableElements[0].focus();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!preventDismiss && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className='modal-overlay'
      data-testid={baseTestId}
      onClick={handleOverlayClick}>
      <div
        {...(heading ? { 'aria-labelledby': headingId } : { 'aria-label': 'Modal' })}
        ref={modalRef}
        aria-modal='true'
        className={`modal-content modal-content--${size}${className ? ` ${className}` : ''}`}
        data-testid={`${baseTestId}-content`}
        role='dialog'
        tabIndex={-1}
        onKeyDown={handleKeyDown}>
        <div
          className='modal-header'
          data-testid={`${baseTestId}-header`}>
          {heading && (
            <h2
              className='modal-heading'
              data-testid={`${baseTestId}-heading`}
              id={headingId}>
              {heading}
            </h2>
          )}
          {!preventDismiss && (
            <button
              aria-label='Close modal'
              className='modal-close-button'
              data-testid={`${baseTestId}-close`}
              type='button'
              onClick={onClose}>
              ×
            </button>
          )}
        </div>
        <div
          className='modal-body'
          data-testid={`${baseTestId}-body`}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default Modal;
